import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Custom hook to prevent navigation when there's unsaved data
 * Works with BrowserRouter (declarative router)
 */
export const useNavigationGuard = (hasUnsavedData, onNavigationAttempt) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Handle browser back/forward/refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedData) {
        e.preventDefault();
        e.returnValue = 'Anda memiliki data yang belum disimpan. Yakin ingin keluar?';
        return e.returnValue;
      }
    };

    const handlePopState = (e) => {
      if (hasUnsavedData) {
        e.preventDefault();
        // Push current state back to prevent navigation
        window.history.pushState(null, '', location.pathname);
        onNavigationAttempt && onNavigationAttempt('back');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedData, location.pathname, onNavigationAttempt]);

  // Handle programmatic navigation attempts
  useEffect(() => {
    const handleLinkClick = (e) => {
      if (!hasUnsavedData) return;

      // Find the closest navigation link
      const navLink = e.target.closest('a[href]');
      if (!navLink) return;

      const href = navLink.getAttribute('href');
      
      // Check if it's an internal navigation to a different route
      if (href && href.startsWith('/') && href !== location.pathname) {
        e.preventDefault();
        e.stopPropagation();
        onNavigationAttempt && onNavigationAttempt(href);
        return false;
      }
    };

    // Attach to document to catch all link clicks
    document.addEventListener('click', handleLinkClick, true);
    return () => document.removeEventListener('click', handleLinkClick, true);
  }, [hasUnsavedData, location.pathname, onNavigationAttempt]);

  // Provide a safe navigate function
  const safeNavigate = useCallback((path, options = {}) => {
    if (hasUnsavedData && !options.force) {
      onNavigationAttempt && onNavigationAttempt(path);
    } else {
      navigate(path);
    }
  }, [hasUnsavedData, navigate, onNavigationAttempt]);

  return { safeNavigate };
};

export default useNavigationGuard;
